import Cookies from "js-cookie";

interface APIRequestParams {
  url: string;
  method: string;
  data?: any;
  contentType?: string;
  signal?: AbortSignal | null;
}

interface APIError {
  data?: any;
  status: number;
  message?: string;
}

interface QueryParams {
  [key: string]: string | number | boolean;
}

interface AutocompleteOption {
  value: number;
  label: string;
  data: any;
}

interface AutocompleteResult {
  id: string;
  name: string;
  [key: string]: any;
}

interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string | null;
  previous?: string | null;
}

/*
 * Make an API request with credentials
 */
async function makeAPIRequest({
  url,
  method,
  data = null,
  contentType = "application/JSON",
  signal = null,
}: APIRequestParams): Promise<any> {
  const _url = url.startsWith("http")
    ? url
    : `${process.env.REACT_APP_API_ROOT}${url}`;
  const params: RequestInit = {
    method,
    credentials: "include",
    headers: {
      "X-CSRFToken": Cookies.get("csrftoken") || "",
      "X-Requested-With": "XMLHttpRequest",
      Accept: "application/json",
      "Content-Type": contentType,
    },
    signal,
  };
  if (data && contentType === "application/json")
    params.body = JSON.stringify(data);
  else if (data) params.body = data;

  // using form data means we need to manually remove Content-Type header
  // https://muffinman.io/uploading-files-using-fetch-multipart-form-data/
  if (contentType === "multipart/form-data")
    delete (params.headers as Record<string, string>)["Content-Type"];

  const response = await fetch(_url, params);
  if (response.ok) {
    if (response.status === 204) {
      return Promise.resolve({});
    }
    return Promise.resolve(response.json());
  } else {
    // handle response differently based on content type
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const errorJSON = await response.json();
      const errorData: APIError = { data: errorJSON, status: response.status };
      return Promise.reject(errorData);
    } else {
      return Promise.reject({
        message: response.statusText,
        status: response.status,
      });
    }
  }
}

/*
 * Make an API request, if access token is expired, refresh it
 */
async function makeRequestAndReturnJSON({
  url,
  method,
  data = null,
  contentType = "application/JSON",
  signal = null,
}: APIRequestParams): Promise<any> {
  try {
    const response = await makeAPIRequest({
      url,
      method,
      data,
      contentType,
      signal,
    });
    return Promise.resolve(response);
  } catch (e: any) {
    // if access token is invalid, refresh it, and try the request again
    if (e.status === 401 && e.data?.code === "token_not_valid") {
      try {
        await makeAPIRequest({ url: "/api/v1/token/refresh/", method: "POST" });
        try {
          const retryResponse = await makeAPIRequest({
            url,
            method,
            data,
            contentType,
            signal,
          });
          return Promise.resolve(retryResponse);
        } catch (retryError) {
          return Promise.reject(retryError);
        }
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    } else {
      return Promise.reject(e);
    }
  }
}

function postAndReturnJSON({
  url,
  data = null,
  method = "POST",
  contentType = "application/json",
}: {
  url: string;
  data?: any;
  method?: string;
  contentType?: string;
}): Promise<any> {
  return makeRequestAndReturnJSON({
    url,
    method,
    data,
    contentType,
  });
}

function getQueryStringFromParams(params: QueryParams): string {
  let queryString = "";
  const entries = Object.entries(params);
  if (entries.length > 0) {
    queryString =
      "?" +
      entries
        .map(([key, value]) => key + "=" + encodeURIComponent(value))
        .join("&");
  }

  return queryString;
}

function getAndReturnJSON({
  url,
  params = {},
  signal = null,
}: {
  url: string;
  params?: QueryParams;
  signal?: AbortSignal | null;
}): Promise<any> {
  // querystring
  const queryString = getQueryStringFromParams(params);
  return makeRequestAndReturnJSON({
    url: `${url}${queryString}`,
    method: "GET",
    signal,
  });
}

function getLoadAutocompleteOptions({
  url,
  customerId,
  showAll = false,
}: {
  url: string;
  customerId?: string | number;
  showAll?: boolean;
}) {
  return async function (
    inputValue: string,
    callback: (options: AutocompleteOption[]) => void,
    isMounted: boolean,
  ): Promise<void> {
    const params: QueryParams = {
      query: inputValue ? inputValue : "",
    };
    if (customerId) params.customer_id = customerId;
    if (showAll) params.show_all = "1";

    const data: PaginatedResponse<AutocompleteResult> = await getAndReturnJSON({
      url,
      params,
    });
    const results = data.results.map((result) => ({
      value: parseInt(result.id),
      label: result.name,
      data: result,
    }));
    if (isMounted) callback(results);
  };
}

function getIdFromAPIUrl(url: string): string | undefined {
  const split = url.split("/");
  return split[split.length - 2];
}

async function getAllPages<T = any>({
  url,
  params,
  limit = 50,
}: {
  url: string;
  params: QueryParams;
  limit?: number;
}): Promise<PaginatedResponse<T>> {
  const _params = Object.assign({}, params, { limit, offset: 0 });
  const response: PaginatedResponse<T> = { results: [], count: 0 };

  let hasNext = true;
  let nextUrl: string | false = false;

  while (hasNext) {
    const _response: PaginatedResponse<T> = nextUrl
      ? await getAndReturnJSON({ url: nextUrl })
      : await getAndReturnJSON({ url, params: _params });
    response.results.push(..._response.results);
    response.count = _response.count;

    if (!_response.next) {
      hasNext = false;
    } else {
      nextUrl = _response.next;
    }
  }
  return response;
}

async function getPaginated<T = any>({
  url,
  params,
  limit = 0,
  offset = 0,
}: {
  url: string;
  params: QueryParams;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<T>> {
  const _params = Object.assign({}, params, { limit, offset });
  const response = await getAndReturnJSON({ url, params: _params });

  return response;
}

export {
  getAndReturnJSON,
  getAllPages,
  getIdFromAPIUrl,
  getLoadAutocompleteOptions,
  getQueryStringFromParams,
  makeRequestAndReturnJSON,
  postAndReturnJSON,
  getPaginated,
};
